import { Application, JobAlert, ResumeDocument } from '../../types';

// The slice of app state we back up / sync per signed-in user. AsyncStorage
// stays the offline source of truth; this is what gets mirrored to Firestore
// at users/{uid}. Job listings (`data`) are NOT synced — they're a shared
// cache refetched from the cloud function.
export interface SyncableState {
  resumes: ResumeDocument[];
  keys: string[]; // favorite job ids
  applied: string[]; // applied job ids
  applications: Record<string, Application>; // tracker records keyed by jobId
  alerts: JobAlert[]; // saved job alerts (push subscriptions)
  themeMode: 'light' | 'dark';
  language: string;
  // When the scalar settings (themeMode/language) last changed locally. Acts
  // as the last-write-wins clock for scalars during a merge.
  updatedAt: string;
}

const unique = (xs: string[]): string[] => Array.from(new Set(xs));

// True when timestamp `a` is strictly newer than `b`. NaN-safe: a missing /
// unparseable timestamp counts as the oldest possible.
const isNewer = (a: string | undefined, b: string | undefined): boolean => {
  const ta = a ? Date.parse(a) : NaN;
  const tb = b ? Date.parse(b) : NaN;
  if (Number.isNaN(ta)) return false;
  if (Number.isNaN(tb)) return true;
  return ta > tb;
};

// Merge resumes by id, keeping the most-recently-updated copy of each and the
// union of ids from both sides.
export const mergeResumes = (
  local: ResumeDocument[],
  remote: ResumeDocument[]
): ResumeDocument[] => {
  const byId = new Map<string, ResumeDocument>();
  for (const r of remote ?? []) byId.set(r.id, r);
  for (const r of local ?? []) {
    const existing = byId.get(r.id);
    if (!existing || isNewer(r.updatedAt, existing.updatedAt)) byId.set(r.id, r);
  }
  return Array.from(byId.values());
};

// Merge application records by jobId, keeping the most-recently-updated copy
// of each and the union of ids from both sides.
export const mergeApplications = (
  local: Record<string, Application>,
  remote: Record<string, Application>
): Record<string, Application> => {
  const out: Record<string, Application> = { ...(remote ?? {}) };
  for (const [id, a] of Object.entries(local ?? {})) {
    const existing = out[id];
    if (!existing || isNewer(a.updatedAt, existing.updatedAt)) out[id] = a;
  }
  return out;
};

// Merge job alerts by id, keeping the most-recently-updated copy of each.
export const mergeAlerts = (local: JobAlert[], remote: JobAlert[]): JobAlert[] => {
  const byId = new Map<string, JobAlert>();
  for (const a of remote ?? []) byId.set(a.id, a);
  for (const a of local ?? []) {
    const existing = byId.get(a.id);
    if (!existing || isNewer(a.updatedAt, existing.updatedAt)) byId.set(a.id, a);
  }
  return Array.from(byId.values());
};

// Local-first merge run when a user signs in (and on each sync). Designed to
// never lose data: resumes merge per-id by updatedAt; favorites/applied are
// unioned; scalar settings follow whichever side was written more recently.
//
// Known v1 limitation: unioning favorites/applied means an un-favorite or
// un-apply on one device can be resurrected by the other. Acceptable for the
// "don't lose data across devices" goal; tombstones can be added later.
export const mergeSyncable = (
  local: SyncableState,
  remote: SyncableState
): SyncableState => {
  const remoteWins = isNewer(remote?.updatedAt, local?.updatedAt);
  return {
    resumes: mergeResumes(local?.resumes ?? [], remote?.resumes ?? []),
    keys: unique([...(local?.keys ?? []), ...(remote?.keys ?? [])]),
    applied: unique([...(local?.applied ?? []), ...(remote?.applied ?? [])]),
    applications: mergeApplications(local?.applications ?? {}, remote?.applications ?? {}),
    alerts: mergeAlerts(local?.alerts ?? [], remote?.alerts ?? []),
    themeMode: remoteWins ? remote.themeMode : local.themeMode,
    language: remoteWins ? remote.language : local.language,
    updatedAt: remoteWins ? remote.updatedAt : local.updatedAt,
  };
};

// Add dummy default export for Expo Router (every file under app/ needs one).
export default { mergeSyncable, mergeResumes, mergeApplications, mergeAlerts };
